import { PermissionCode, ProductCode } from '@gb-ticket/shared-types';
import { type RouteObject } from 'react-router';

import { RequireAuthLayout, RequireProductLayout } from '@/platform/auth/require-permission';
import { PlatformShellLayout } from '@/platform/layouts/platform-shell';
import '@/products/maintenance/product-nav';
import { MaintenanceAdminGate } from '@/products/maintenance/features/maintenance-admin-gate';
import { MaintenanceLayout } from '@/products/maintenance/layouts/maintenance-layout';
import { MAINTENANCE_ROOT } from '@/products/maintenance/maintenance-paths';
import { MaintenanceCustomerDetailPage } from '@/products/maintenance/pages/maintenance-customer-detail-page';
import { MaintenanceCustomerFormPage } from '@/products/maintenance/pages/maintenance-customer-form-page';
import { MaintenanceCustomersPage } from '@/products/maintenance/pages/maintenance-customers-page';
import { MaintenanceHomePage } from '@/products/maintenance/pages/maintenance-home-page';
import { MaintenanceInstallationDetailPage } from '@/products/maintenance/pages/maintenance-installation-detail-page';
import { MaintenanceInstallationFormPage } from '@/products/maintenance/pages/maintenance-installation-form-page';
import { MaintenanceProjectDetailPage } from '@/products/maintenance/pages/maintenance-project-detail-page';
import { MaintenanceProjectFormPage } from '@/products/maintenance/pages/maintenance-project-form-page';
import { MaintenanceTemplateFormPage } from '@/products/maintenance/pages/maintenance-template-form-page';
import { MaintenanceWorkOrderFormPage } from '@/products/maintenance/pages/maintenance-work-order-form-page';
import { MaintenanceTemplateDetailPage } from '@/products/maintenance/pages/maintenance-template-detail-page';
import { MaintenanceTemplatesPage } from '@/products/maintenance/pages/maintenance-templates-page';
import { MaintenanceTemplateVersionPage } from '@/products/maintenance/pages/maintenance-template-version-page';
import { MaintenancePlanningPage } from '@/products/maintenance/pages/maintenance-planning-page';
import { MaintenanceTrashPage } from '@/products/maintenance/pages/maintenance-trash-page';
import { MaintenanceWorkOrderDetailPage } from '@/products/maintenance/pages/maintenance-work-order-detail-page';
import { MaintenanceProtocolWizardPage } from '@/products/maintenance/pages/maintenance-protocol-wizard-page';
import { MaintenanceArchivePage } from '@/products/maintenance/pages/maintenance-archive-page';
import { MaintenanceProtocolArchivePage } from '@/products/maintenance/pages/maintenance-protocol-archive-page';

/** Maintenance product (`/maintenance`) inside the Mitarbeiterportal chrome. */
export const maintenanceRoute: RouteObject = {
  Component: RequireAuthLayout,
  children: [
    {
      Component: PlatformShellLayout,
      children: [
        {
          path: MAINTENANCE_ROOT,
          element: <RequireProductLayout product={ProductCode.MAINTENANCE} />,
          children: [
            {
              Component: MaintenanceLayout,
              children: [
                { index: true, Component: MaintenanceHomePage },
                { path: 'auftraege/:workOrderId', Component: MaintenanceWorkOrderDetailPage },
                { path: 'protokolle/:protocolId', Component: MaintenanceProtocolWizardPage },
                {
                  element: (
                    <MaintenanceAdminGate
                      permission={PermissionCode.MAINTENANCE_PROTOCOLS_READ_ALL}
                    />
                  ),
                  children: [
                    { path: 'archiv', Component: MaintenanceArchivePage },
                    {
                      path: 'protokolle/:protocolId/archiv',
                      Component: MaintenanceProtocolArchivePage,
                    },
                  ],
                },
                {
                  element: (
                    <MaintenanceAdminGate permission={PermissionCode.MAINTENANCE_ORDERS_MANAGE} />
                  ),
                  children: [
                    { path: 'planung', Component: MaintenancePlanningPage },
                    { path: 'papierkorb', Component: MaintenanceTrashPage },
                    { path: 'planung/neu', Component: MaintenanceWorkOrderFormPage },
                    {
                      path: 'planung/:workOrderId/bearbeiten',
                      Component: MaintenanceWorkOrderFormPage,
                    },
                  ],
                },
                {
                  element: (
                    <MaintenanceAdminGate
                      permission={PermissionCode.MAINTENANCE_MASTER_DATA_MANAGE}
                    />
                  ),
                  children: [
                    { path: 'kunden', Component: MaintenanceCustomersPage },
                    { path: 'kunden/neu', Component: MaintenanceCustomerFormPage },
                    {
                      path: 'kunden/:customerId/bearbeiten',
                      Component: MaintenanceCustomerFormPage,
                    },
                    {
                      path: 'kunden/:customerId/projekte/neu',
                      Component: MaintenanceProjectFormPage,
                    },
                    { path: 'kunden/:customerId', Component: MaintenanceCustomerDetailPage },
                    {
                      path: 'projekte/:projectId/bearbeiten',
                      Component: MaintenanceProjectFormPage,
                    },
                    {
                      path: 'projekte/:projectId/anlagen/neu',
                      Component: MaintenanceInstallationFormPage,
                    },
                    { path: 'projekte/:projectId', Component: MaintenanceProjectDetailPage },
                    {
                      path: 'anlagen/:installationId/bearbeiten',
                      Component: MaintenanceInstallationFormPage,
                    },
                    {
                      path: 'anlagen/:installationId',
                      Component: MaintenanceInstallationDetailPage,
                    },
                  ],
                },
                {
                  element: (
                    <MaintenanceAdminGate
                      permission={PermissionCode.MAINTENANCE_TEMPLATES_MANAGE}
                    />
                  ),
                  children: [
                    { path: 'vorlagen', Component: MaintenanceTemplatesPage },
                    { path: 'vorlagen/neu', Component: MaintenanceTemplateFormPage },
                    { path: 'vorlagen/:templateId', Component: MaintenanceTemplateDetailPage },
                    {
                      path: 'vorlagen/:templateId/versionen/:versionId',
                      Component: MaintenanceTemplateVersionPage,
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
  ],
};
